/**
 * Share the results — the rules with no React in them: the payload the API gives for a finished
 * event, the timing of the completion screen's sequence, and how the card image is handed to the
 * phone's share sheet (or downloaded where there is none).
 */

export interface ResultsLeg {
    game_uid: string;
    /** "Final", "Semi-final", "Round 2", "Matchday 5". */
    round: string;
    opponent: string;
    score: string;
    won: boolean;
    outcome?: string;
    photos?: string[];
    opponent_photos?: string[];
    played_at: string | null;
}

export interface ResultsPodium {
    place: number;
    label: string;
    photos: string[];
    /** "12 pts", "4 wins" — a league's measure; null for a tournament. */
    metric: string | null;
}

export interface ResultsPayload {
    event: {
        uid: string;
        type: 'tournament' | 'league';
        title: string;
        sport_type: string | null;
        sport_label: string | null;
        starts_at: string | null;
        ends_at: string | null;
        finished_at: string | null;
        version: number;
        is_private: boolean;
        /** The organiser's club is branded: the card carries their logo, the page no "Run your own". */
        branded: boolean;
    };
    kind: 'tournament' | 'league' | 'social';
    champion: {key: string; label: string; photos: string[]; decided_by: 'final' | 'standings'};
    podium: ResultsPodium[];
    route: ResultsLeg[];
    message: string;
    whatsapp_url: string;
    public_url: string;
    card: {feed: string; story: string};
    claim_line: string;
}

export type ShareChannel = 'whatsapp' | 'copy' | 'image' | 'story';

/** Remembers that this browser has seen an event's "Share the results", so it opens itself once. */
const seenKey = (uid: string) => `sp_share_seen_${uid}`;

export function shareSeen(uid: string): boolean {
    try {
        return localStorage.getItem(seenKey(uid)) === '1';
    } catch {
        return true; // private mode: never nag
    }
}

export function markShareSeen(uid: string): void {
    try { localStorage.setItem(seenKey(uid), '1'); } catch { /* storage off */ }
}

export function prefersReducedMotion(): boolean {
    try {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
        return false;
    }
}

/**
 * When each beat of the completion screen arrives, in seconds: the champion's road first, the
 * podium beneath it, the card, then the share buttons last so the eye lands on them. The whole
 * sequence is under two seconds; with reduced motion everything is simply there.
 */
export function sequence(reduced: boolean): {road: number; legStagger: number; podium: number; card: number; buttons: number} {
    if (reduced) return {road: 0, legStagger: 0, podium: 0, card: 0, buttons: 0};
    return {road: 0, legStagger: 0.08, podium: 0.8, card: 1.1, buttons: 1.5};
}

/** The heading the podium wears: a league is a final table, a social night tonight's places. */
export function resultsHeading(kind: ResultsPayload['kind']): string {
    return kind === 'league' ? 'Final table' : kind === 'social' ? "Tonight's results" : 'Final results';
}

/** The road's heading: a tournament's road to victory, a league champion's season. */
export function roadHeading(kind: ResultsPayload['kind']): string {
    return kind === 'league' ? 'The season' : 'Road to victory';
}

/** The file name a shared card carries. */
export function cardFileName(payload: Pick<ResultsPayload, 'event'>, size: 'feed' | 'story'): string {
    const slug = (payload.event.title || 'results').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'results';
    return `${slug}-results${size === 'story' ? '-story' : ''}.png`;
}

/** "Run your own" for a Free club's page: the quiz, naming the sport, marked as coming from a results page. */
export function runYourOwnHref(sport: string | null | undefined): string {
    return `/quiz/?${sport ? `sport=${encodeURIComponent(sport)}&` : ''}src=results`;
}

/**
 * The card as a file to the phone's share sheet (Instagram, WhatsApp and the rest appear), or a
 * download where the device cannot share files (a desktop). The PNG is fetched through the API's
 * card URL; a fetch the browser refuses falls back to opening the card itself.
 */
export async function shareImage(url: string, fileName: string, title: string): Promise<'shared' | 'downloaded' | 'opened'> {
    let blob: Blob;
    try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        blob = await response.blob();
    } catch {
        window.open(url, '_blank', 'noopener');
        return 'opened';
    }
    const file = new File([blob], fileName, {type: 'image/png'});
    const nav = navigator as Navigator & {canShare?: (d: unknown) => boolean};
    if (nav.share && nav.canShare?.({files: [file]})) {
        try {
            await nav.share({files: [file], title});
            return 'shared';
        } catch (e: any) {
            if (e?.name === 'AbortError') return 'shared';
        }
    }
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = fileName;
    a.click();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    return 'downloaded';
}
